/**
 * Installation Upsell Toggle Component
 *
 * Manages the installation upsell toggle on the product page.
 * When the toggle is ON and the customer adds the main product to cart,
 * the installation variant ID is set on the product form via a data attribute.
 * ProductFormComponent reads this and sends BOTH items in a single atomic
 * /cart/add.js request using the Shopify items[] format — no race conditions.
 */
class InstallationUpsellComponent extends HTMLElement {
  /** @type {HTMLInputElement | null} */
  #toggle = null;

  /** @type {HTMLElement | null} */
  #summary = null;

  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    this.#toggle = this.querySelector('[ref="toggleInput"]');
    this.#summary = this.querySelector('[ref="summary"]');
    this.#form = this.closest('.buy-buttons-block')?.querySelector('form') || null;

    const { signal } = this.#abortController;

    // Listen for variant:update on the section (these always fire within the section)
    const section = this.closest('.shopify-section');
    if (section) {
      section.addEventListener('variant:update', this.#onVariantUpdate, { signal });
    }

    // Listen for toggle changes
    if (this.#toggle) {
      this.#toggle.addEventListener('change', this.#onToggleChange, { signal });
    }

    // Apply default checked state (e.g. for VIGI/Omada products)
    if (this.dataset.defaultChecked === 'true' && this.#toggle?.checked) {
      this.#updateSummaryVisibility();
      if (this.#form) {
        this.#form.dataset.installVariantId = this.dataset.installVariantId;
      }
    }
  }

  disconnectedCallback() {
    this.#abortController.abort();
    // Clean up the data attribute so stale state never leaks
    if (this.#form) {
      delete this.#form.dataset.installVariantId;
    }
  }

  /**
   * Updates the summary line visibility based on toggle state.
   */
  #updateSummaryVisibility() {
    if (!this.#summary || !this.#toggle) return;

    if (this.#toggle.checked) {
      this.#summary.classList.add('is-visible');
    } else {
      this.#summary.classList.remove('is-visible');
    }
  }

  /**
   * Handles toggle change — sets or removes the installation variant ID
   * on the product form so ProductFormComponent can include it in a single
   * multi-item add request.
   */
  #onToggleChange = () => {
    this.#updateSummaryVisibility();

    if (this.#form) {
      if (this.#toggle?.checked) {
        this.#form.dataset.installVariantId = this.dataset.installVariantId;
      } else {
        delete this.#form.dataset.installVariantId;
      }
    }
  };

  /**
   * Handles variant:update events to update the product price in the summary line.
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    const detail = /** @type {any} */ (event).detail;
    if (!detail?.resource) return;

    // Check the event is for our product
    if (detail.data?.productId && detail.data.productId !== this.dataset.productId) return;

    const variant = detail.resource;
    if (!variant || !variant.price) return;

    // Update the product price display in the summary
    const priceEl = this.querySelector('[ref="productPrice"]');
    if (priceEl && variant.price) {
      const formattedPrice = this.#formatMoney(variant.price);
      if (formattedPrice) {
        priceEl.textContent = formattedPrice;
        this.dataset.productPrice = formattedPrice;
      }
    }
  };

  /**
   * Formats a price in cents to the store's currency format.
   * @param {number} cents - Price in cents
   * @returns {string} Formatted price string
   */
  #formatMoney(cents) {
    if (typeof cents !== 'number') return '';

    // Use Intl.NumberFormat for locale-aware formatting
    // The store uses EUR based on the existing price displays
    try {
      return new Intl.NumberFormat('nl-NL', {
        style: 'currency',
        currency: 'EUR',
      }).format(cents / 100);
    } catch {
      return `€${(cents / 100).toFixed(2).replace('.', ',')}`;
    }
  }
}

if (!customElements.get('installation-upsell-component')) {
  customElements.define('installation-upsell-component', InstallationUpsellComponent);
}
